import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  GET_EMPLOYMENT_VERIFICATION,
  SUBMIT_EMPLOYMENT_VERIFICATION
} from 'dal/employmentVerification';

const STATUS_MESSAGES = {
  responded: 'Thank you — your response has already been recorded.',
  expired: 'This verification link has expired.',
  skipped: 'This verification has been marked as skipped by the property management company.',
  manually_completed:
    'This verification has been marked as complete by the property management company.'
};

const EmploymentVerificationPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: null,
    verification: null,
    questions: [],
    submitted: false
  });

  useEffect(() => {
    GET_EMPLOYMENT_VERIFICATION({ token })
      .then(({ verification, questions }) => {
        setState((s) => ({ ...s, loading: false, verification, questions }));
      })
      .catch((err) => {
        setState((s) => ({
          ...s,
          loading: false,
          error: err?.message || 'Unable to load this verification.'
        }));
      });
  }, [token]);

  const handleSubmit = (answers, supervisorName) => {
    return SUBMIT_EMPLOYMENT_VERIFICATION({ token, data: { answers, supervisorName } }).then(() =>
      setState((s) => ({ ...s, submitted: true }))
    );
  };

  if (state.loading)
    return (
      <div style={styles.container}>
        <p style={styles.message}>Loading…</p>
      </div>
    );

  if (state.error)
    return (
      <div style={styles.container}>
        <p style={styles.message}>{state.error}</p>
      </div>
    );

  if (state.submitted) {
    return (
      <div style={styles.container}>
        <h2 style={styles.heading}>Thank you!</h2>
        <p style={styles.message}>
          Your response has been recorded. The property management company will be notified.
        </p>
      </div>
    );
  }

  const terminalMessage = STATUS_MESSAGES[state.verification?.status];
  if (terminalMessage)
    return (
      <div style={styles.container}>
        <p style={styles.message}>{terminalMessage}</p>
      </div>
    );

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Employment History Verification</h2>
      <p style={styles.subheading}>
        You have been asked to verify the employment history
        {state.verification?.applicantName ? (
          <>
            {' '}
            of <strong>{state.verification.applicantName}</strong>
          </>
        ) : (
          ' of a former employee'
        )}{' '}
        at <strong>{state.verification?.employerSummary}</strong>.
      </p>
      <EmploymentVerificationForm
        questions={state.questions}
        supervisorName={state.verification?.supervisorName}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

const EmploymentVerificationForm = ({ questions, supervisorName, onSubmit }) => {
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState(supervisorName || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = questions.filter((q) => q.required && !answers[q.key]);
    if (missing.length) {
      setError('Please answer all required questions.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(answers, name);
      setSubmitting(false);
    } catch (error) {
      console.error('Employment verification submission failed:', error);
      setError('Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.field}>
        <label style={styles.label}>Your name</label>
        <input
          style={styles.input}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
        />
      </div>

      {questions.map((q) => (
        <div key={q.key} style={styles.field}>
          <label htmlFor={`q-${q.key}`} style={styles.label}>
            {q.label}
            {q.required && <span style={styles.required}> *</span>}
          </label>
          {q.type === 'yes_no' && (
            <div style={styles.radioGroup}>
              {['Yes', 'No'].map((opt) => (
                <label key={opt} style={styles.radioLabel}>
                  <input
                    type="radio"
                    name={q.key}
                    value={opt}
                    checked={answers[q.key] === opt}
                    onChange={() => handleChange(q.key, opt)}
                  />{' '}
                  {opt}
                </label>
              ))}
            </div>
          )}
          {q.type === 'text' && (
            <input
              id={`q-${q.key}`}
              style={styles.input}
              type="text"
              value={answers[q.key] || ''}
              onChange={(e) => handleChange(q.key, e.target.value)}
              aria-required={q.required}
            />
          )}
          {q.type === 'textarea' && (
            <textarea
              id={`q-${q.key}`}
              style={styles.textarea}
              value={answers[q.key] || ''}
              onChange={(e) => handleChange(q.key, e.target.value)}
              rows={4}
              aria-required={q.required}
            />
          )}
        </div>
      ))}

      {error && <p style={styles.error}>{error}</p>}

      <button type="submit" style={styles.button} disabled={submitting}>
        {submitting ? 'Submitting…' : 'Submit Verification'}
      </button>
    </form>
  );
};

const styles = {
  container: { maxWidth: 600, margin: '40px auto', padding: '0 20px', fontFamily: 'sans-serif' },
  heading: { fontSize: 22, fontWeight: 600, marginBottom: 8 },
  subheading: { color: '#555', marginBottom: 24 },
  message: { color: '#555', fontSize: 16 },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontWeight: 500, fontSize: 14 },
  required: { color: '#c00' },
  input: { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 },
  textarea: {
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 14,
    resize: 'vertical'
  },
  radioGroup: { display: 'flex', gap: 20 },
  radioLabel: { fontSize: 14, cursor: 'pointer' },
  error: { color: '#c00', fontSize: 13 },
  button: {
    padding: '10px 20px',
    background: '#1a56db',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 15,
    cursor: 'pointer',
    alignSelf: 'flex-start'
  }
};

export default EmploymentVerificationPage;
